import { useEffect, useState } from 'react';
import { Entity } from '@backstage/catalog-model';
import { createApiRef, useApiHolder } from '@backstage/core-plugin-api';
import { toSafeHref, trimTrailingSlashes } from '../shared/links';
import { sharedLoad } from './cache';
import { PROMOTION_TARGETS, PromotionTarget } from './templates';

/**
 * Argo CD state of a component's environments (AC-2), for the Ambientes and
 * Links cards.
 *
 * `@backstage-community/plugin-argocd` registers its client under the ApiRef
 * id below but does not export the ref. This file reaches the same
 * registered client through a local ref with that id: same client, same
 * backend proxy, no new endpoint and no raw fetch (SR-08). Every value read
 * from the client goes through a runtime guard, and the commit author's
 * email is never read into the model (MC-5). A test checks that the
 * installed plugin still registers this id.
 */
export const ARGOCD_API_ID = 'plugin.argo.cd.service';

/** The two client methods this adapter calls, as the plugin declares them. */
export interface ArgoCdServiceClient {
  listApps(options: {
    url: string;
    appSelector: string;
    appNamespace?: string;
    projectName?: string;
  }): Promise<unknown>;
  getRevisionDetails(options: {
    app: string;
    appNamespace?: string;
    instanceName: string;
    revisionID: string;
  }): Promise<unknown>;
}

export const argoCdServiceApiRef = createApiRef<ArgoCdServiceClient>({
  id: ARGOCD_API_ID,
});

/** Annotations written by the backend `byx-catalog` (deploymentAnnotations). */
export const DEPLOYED_ENVIRONMENTS_ANNOTATION =
  'byxcapital.com/deployed-environments';
export const ARGOCD_SELECTOR_ANNOTATION = 'argocd/app-selector';
export const ARGOCD_INSTANCE_ANNOTATION = 'argocd/instance-name';

/** The environments of the Ambientes card, in promotion order. */
export const ENVIRONMENTS = ['dev', 'staging', 'prod'] as const;

export type Environment = (typeof ENVIRONMENTS)[number];

export type EnvironmentState = 'synced' | 'outOfSync' | 'notDeployed';

export type Revision = {
  sha: string;
  message?: string;
  authorName?: string;
};

export type EnvironmentRow = {
  env: Environment;
  state: EnvironmentState;
  revision?: Revision;
};

export type EnvironmentsResult = {
  rows: EnvironmentRow[];
  /** Web address of the Argo CD instance, when the client returns one. */
  instanceUrl?: string;
};

export type EnvironmentsState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; value: EnvironmentsResult };

function annotation(entity: Entity, key: string): string | undefined {
  const value = entity.metadata.annotations?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

/** Environments listed in `byxcapital.com/deployed-environments`. */
export function deployedEnvironments(entity: Entity): Set<string> {
  const raw = annotation(entity, DEPLOYED_ENVIRONMENTS_ANNOTATION) ?? '';
  return new Set(
    raw
      .split(',')
      .map(env => env.trim())
      .filter(Boolean),
  );
}

/** True when the component has the annotation the Ambientes card needs. */
export function hasDeployedEnvironments(entity: Entity): boolean {
  return deployedEnvironments(entity).size > 0;
}

/**
 * Where "Promover" is offered: each of staging and prod that is not
 * deployed while an earlier environment (dev → staging → prod) is. The same
 * rule as the backend's `promotionLinks` (byx-catalog.ts).
 */
export function promotionTargets(deployed: Set<string>): PromotionTarget[] {
  return PROMOTION_TARGETS.filter(
    target =>
      !deployed.has(target) &&
      ENVIRONMENTS.slice(0, ENVIRONMENTS.indexOf(target)).some(env =>
        deployed.has(env),
      ),
  );
}

// --- Runtime guards over the client's responses -------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function own(value: unknown, key: string): unknown {
  return isRecord(value) && Object.hasOwn(value, key) ? value[key] : undefined;
}

function stringAt(value: unknown, ...path: string[]): string | undefined {
  const found = path.reduce<unknown>((node, key) => own(node, key), value);
  return typeof found === 'string' && found.trim() ? found.trim() : undefined;
}

/** A git commit id (a helm chart version is not a revision we can show). */
const SHA = /^[0-9a-f]{7,64}$/i;

type ArgoApp = {
  name: string;
  synced: boolean;
  sha?: string;
};

function readApp(value: unknown): ArgoApp | undefined {
  const name = stringAt(value, 'metadata', 'name');
  if (!name) {
    return undefined;
  }
  const revisions = own(own(own(value, 'status'), 'sync'), 'revisions');
  const firstRevision = Array.isArray(revisions) ? revisions[0] : undefined;
  const sha =
    stringAt(value, 'status', 'sync', 'revision') ??
    (typeof firstRevision === 'string' ? firstRevision : undefined);
  return {
    name,
    synced: stringAt(value, 'status', 'sync', 'status') === 'Synced',
    ...(sha && SHA.test(sha) ? { sha } : {}),
  };
}

/** The applications in a `listApps` response (`{ items: [...] }`). */
export function readApps(response: unknown): ArgoApp[] {
  const items = own(response, 'items');
  if (!Array.isArray(items)) {
    return [];
  }
  return items.map(readApp).filter((app): app is ArgoApp => app !== undefined);
}

/** The instance's web address, if the response carries one. */
export function readInstanceUrl(response: unknown): string | undefined {
  const items = own(response, 'items');
  if (!Array.isArray(items)) {
    return undefined;
  }
  for (const item of items) {
    const url = toSafeHref(stringAt(item, 'metadata', 'instance', 'url'));
    if (url?.startsWith('https://')) {
      return trimTrailingSlashes(url);
    }
  }
  return undefined;
}

/**
 * The author's name from a git author line ("Name <email>"). The email is
 * cut before anything else reads the value, and a bare email is dropped
 * (MC-5).
 */
export function authorName(author: unknown): string | undefined {
  if (typeof author !== 'string') {
    return undefined;
  }
  const name = author.split('<')[0].trim();
  return name && !name.includes('@') ? name : undefined;
}

/** First line of a commit message, at most 200 characters. */
function firstLine(message: unknown): string | undefined {
  if (typeof message !== 'string') {
    return undefined;
  }
  const line = message.split('\n')[0].trim();
  return line ? line.slice(0, 200) : undefined;
}

/** Message and author name of a revision, from `getRevisionDetails`. */
export function readRevisionDetails(response: unknown): Omit<Revision, 'sha'> {
  const message = firstLine(own(response, 'message'));
  const name = authorName(own(response, 'author'));
  return {
    ...(message ? { message } : {}),
    ...(name ? { authorName: name } : {}),
  };
}

/** The app directory in `byxcapital.com/app=<app>`. */
function selectorApp(selector: string): string | undefined {
  const value = selector.split('=')[1]?.trim();
  return value || undefined;
}

/**
 * The environment of an Argo CD application of this app. The byx-apps
 * ApplicationSets name them `<app>-<env>`, and plain `<app>` in dev; the
 * `-infra-<env>` applications carry the same label and are skipped.
 */
export function environmentOf(
  appName: string,
  app: string | undefined,
): Environment | undefined {
  if (/-infra(-|$)/.test(appName)) {
    return undefined;
  }
  if (app && appName === app) {
    return 'dev';
  }
  return ENVIRONMENTS.find(env => appName.endsWith(`-${env}`));
}

/** One row per environment from the applications found. */
export function environmentRows(
  apps: ArgoApp[],
  app: string | undefined,
): Array<EnvironmentRow & { appName?: string }> {
  return ENVIRONMENTS.map(env => {
    const matching = apps.filter(item => environmentOf(item.name, app) === env);
    if (matching.length === 0) {
      return { env, state: 'notDeployed' as const };
    }
    const withSha = matching.find(item => item.sha);
    return {
      env,
      state: matching.every(item => item.synced)
        ? ('synced' as const)
        : ('outOfSync' as const),
      ...(withSha?.sha
        ? { revision: { sha: withSha.sha }, appName: withSha.name }
        : {}),
    };
  });
}

/**
 * True when Argo CD returned no application of this component (none at all,
 * or only `-infra-` / unmapped ones) while `deployed-environments` lists at
 * least one environment. That is a failed load, never "Não implantado"
 * (Architect A-2, D-12): the `byxcapital.com/app` label the selector uses
 * only exists once the platform PR lands.
 */
export function isEmptyAnswer(
  rows: EnvironmentRow[],
  deployed: Set<string>,
): boolean {
  return deployed.size > 0 && rows.every(row => row.state === 'notDeployed');
}

/**
 * Loads the Argo CD state of the entity's environments. Throws (the cards'
 * error state) when the annotations are missing, a call fails, or Argo CD
 * returns no application for a component that is deployed somewhere.
 */
export async function loadEnvironments(
  client: ArgoCdServiceClient,
  entity: Entity,
): Promise<EnvironmentsResult> {
  const selector = annotation(entity, ARGOCD_SELECTOR_ANNOTATION);
  const instanceName = annotation(entity, ARGOCD_INSTANCE_ANNOTATION);
  if (!selector || !instanceName) {
    throw new Error('Argo CD annotations missing');
  }
  // The client puts both values in the URL path as given: the instance name
  // is encoded once here, the same way for both calls (code O6).
  const instance = encodeURIComponent(instanceName);
  const response = await client.listApps({
    url: `/argoInstance/${instance}`,
    appSelector: selector,
  });
  const rows = environmentRows(readApps(response), selectorApp(selector));
  if (isEmptyAnswer(rows, deployedEnvironments(entity))) {
    throw new Error('Argo CD returned no application');
  }
  const detailed = await Promise.all(
    rows.map(async ({ appName, ...row }) => {
      if (!row.revision || !appName) {
        return row;
      }
      try {
        const details = await client.getRevisionDetails({
          app: appName,
          instanceName: instance,
          revisionID: row.revision.sha,
        });
        return {
          ...row,
          revision: { ...row.revision, ...readRevisionDetails(details) },
        };
      } catch {
        // The revision still shows; only its tooltip is missing.
        return row;
      }
    }),
  );
  const instanceUrl = readInstanceUrl(response);
  return { rows: detailed, ...(instanceUrl ? { instanceUrl } : {}) };
}

/**
 * The Argo CD state of the entity's dev, staging and prod. Reports an error
 * state when the client is not installed, the annotations are missing, a
 * call fails, or Argo CD returns no application for a deployed component
 * (`isEmptyAnswer`). Ambientes and Links share one load.
 */
export function useEnvironments(entity: Entity): EnvironmentsState {
  const client = useApiHolder().get(argoCdServiceApiRef);
  const selector = annotation(entity, ARGOCD_SELECTOR_ANNOTATION) ?? '';
  const instance = annotation(entity, ARGOCD_INSTANCE_ANNOTATION) ?? '';
  const [state, setState] = useState<EnvironmentsState>({ status: 'loading' });

  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    const load = client
      ? sharedLoad(`argocd|${instance}|${selector}`, () =>
          loadEnvironments(client, entity),
        )
      : Promise.reject(new Error('Argo CD client missing'));
    load.then(
      value => active && setState({ status: 'ready', value }),
      () => active && setState({ status: 'error' }),
    );
    return () => {
      active = false;
    };
    // The load depends on the client and the two annotations only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, instance, selector]);

  return state;
}
